package com.nonopn.skydome.common.app.pages.promocodes

import com.nonopn.skydome.common.app.api.impl.AdminPromotionCode
import com.nonopn.skydome.common.app.api.impl.AdminPromotionCodeRequest

// Conversions between the API models of the promotion codes and the pure logic (PromoCodeLogic.kt).

fun AdminPromotionCode.resolvedState(nowSeconds: Long): PromoCodeState = resolvePromoCodeState(
    serverState = state,
    active = active,
    validUntilSecondsUtc = validUntilSecondsUtc,
    redemptions = redemptions,
    maxRedemptions = maxRedemptions,
    nowSeconds = nowSeconds,
)

fun AdminPromotionCode.benefit(): PromoBenefit = promoBenefit(percentOff, freeMonths)

fun AdminPromotionCode.commissionTotals(): List<PromoCommissionTotal> = commissions.map {
    PromoCommissionTotal(it.currency, it.invoices, it.paidAmount, it.commissionAmount)
}

/**
 * The COMPLETE body of a code read from the GET, with only [active] changed (disable / enable). Every other
 * field is sent back as read: otherwise the server would remove a limit, an end date or the referrer.
 */
fun AdminPromotionCode.toRequest(active: Boolean = this.active): AdminPromotionCodeRequest = AdminPromotionCodeRequest(
    uuid = uuid,
    app = app,
    code = code,
    active = active,
    percentOff = percentOff,
    freeMonths = freeMonths,
    maxRedemptions = maxRedemptions,
    validUntilSecondsUtc = validUntilSecondsUtc,
    // As read: the email, or null when the GET gave none. Never "" here: it would remove the referrer.
    referrerEmail = referrer?.email?.takeIf { it.isNotBlank() },
    // Always sent (as read): omitted, the server would erase the commission
    commissionPercent = commissionPercent,
)

/** Form of an existing code: every field read from the GET, so that the save sends them all back. */
fun AdminPromotionCode.toForm(offsetSeconds: Long): PromoCodeForm {
    val email = referrer?.email.orEmpty()
    return PromoCodeForm(
        uuid = uuid,
        app = app,
        code = code,
        benefitKind = benefitKindOf(percentOff, freeMonths),
        percentOff = percentOff?.toString().orEmpty(),
        freeMonths = freeMonths?.toString().orEmpty(),
        maxRedemptions = maxRedemptions?.toString().orEmpty(),
        endDate = validUntilSecondsUtc?.let { formatPromoEndDate(it, offsetSeconds) }.orEmpty(),
        referral = referrer != null || email.isNotBlank() || commissionPercent != null,
        referrerEmail = email,
        commissionPercent = commissionPercent?.toString().orEmpty(),
        active = active,
        originalHasReferrer = referrer != null,
        originalReferrerEmail = referrer?.email?.takeIf { it.isNotBlank() },
        originalCode = code,
        originalValidUntilSecondsUtc = validUntilSecondsUtc,
        redemptions = redemptions,
    )
}

fun PromoCodeSave.toRequest(): AdminPromotionCodeRequest = AdminPromotionCodeRequest(
    uuid = uuid,
    app = app,
    code = code,
    active = active,
    percentOff = percentOff,
    freeMonths = freeMonths,
    maxRedemptions = maxRedemptions,
    validUntilSecondsUtc = validUntilSecondsUtc,
    referrerEmail = referrerEmail,
    commissionPercent = commissionPercent,
)

/** True when changing this code would erase its referral (see [isReferrerWithoutEmail]). */
fun AdminPromotionCode.referrerLocked(): Boolean =
    isReferrerWithoutEmail(referrer != null, referrer?.email, commissionPercent)
